import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import {
  Sparkle, Calculator, ArrowLeft, Sun, Moon as MoonIcon, Star,
  Heart, Gift, ForkKnife, Hand, ChartBar, CaretDown,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import PaywallGate from "../components/PaywallGate";
import ToolAstrologerCta from "../components/ToolAstrologerCta";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";
import { useSavedReportLoader } from "../lib/useSavedReport";
import PlaceAutocompleteInput from "../components/PlaceAutocompleteInput";

const FREE_PLANET_COUNT = 3;  // Sun + Moon + Mars are free; the other 6 are premium

export default function PlanetEffectsPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ date: "", time: "", place: "", timezone: "Asia/Kolkata" });
  usePrefillBirthDetails(setForm);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useSavedReportLoader({ expectedTool: "planet_effects", setForm, setResult });

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const analyze = async (e) => {
    e?.preventDefault();
    if (!form.date || !form.time || !form.place) {
      toast.error("Please enter your date, time and place of birth.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/planet-effects/compute", form);
      setResult(data);
      setTimeout(() => {
        document.querySelector("[data-testid='pe-result']")?.scrollIntoView({
          behavior: "smooth", block: "start",
        });
      }, 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not compute your planetary effects. Please re-check your inputs.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#FAF4E8] min-h-[100dvh] flex flex-col" data-testid="planet-effects-page">
      <Header />
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        <button onClick={() => navigate(-1)} className="md:hidden flex items-center gap-1 text-sm text-muted mb-4" data-testid="pe-back-btn">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="flex items-center gap-2 text-gold label-eyebrow">
          <Sparkle size={14} weight="duotone" /> Free preview · 9-graha effects & remedies
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
          9-Planet Effects <span className="italic text-gold">& Remedies</span>
        </h1>
        <p className="text-muted text-sm sm:text-base mt-4 max-w-2xl font-light">
          Every planet in your kundali — Sun through Ketu — sits in one of the 12 houses and one of the 12 signs. That placement decides how its gifts and shadows actually show up in your life. This tool reads each placement and prescribes a <b className="text-[#3F2D1B]">classical remedy block</b> per planet: mantra, weekday, gemstone, charity, food, behaviour & fast.
        </p>

        {/* Form */}
        <form onSubmit={analyze} className="mt-8 card-surface rounded-md p-5 sm:p-7 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="label-eyebrow text-xs">Date of birth</span>
              <input type="date" required value={form.date} onChange={handle("date")} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="pe-date" />
            </label>
            <label className="block">
              <span className="label-eyebrow text-xs">Time of birth</span>
              <input type="time" required value={form.time} onChange={handle("time")} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="pe-time" />
            </label>
            <label className="block sm:col-span-2">
              <span className="label-eyebrow text-xs">Place of birth</span>
              <div className="mt-1.5">
                <PlaceAutocompleteInput
                  value={form.place}
                  onChange={(v) => setForm((f) => ({ ...f, place: v }))}
                  onPick={({ name, timezone }) =>
                    setForm((f) => ({ ...f, place: name, timezone: timezone || f.timezone }))
                  }
                  placeholder="Mumbai, India"
                  testId="pe-place"
                />
              </div>
            </label>
            <label className="block">
              <span className="label-eyebrow text-xs">Timezone</span>
              <select value={form.timezone} onChange={handle("timezone")} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="pe-tz">
                <option value="Asia/Kolkata">India (IST · UTC+5:30)</option>
                <option value="Asia/Dubai">UAE (UTC+4)</option>
                <option value="Asia/Karachi">Pakistan (UTC+5)</option>
                <option value="Asia/Dhaka">Bangladesh (UTC+6)</option>
                <option value="Europe/London">London (UTC+0/+1)</option>
                <option value="America/New_York">New York (UTC-5/-4)</option>
                <option value="America/Los_Angeles">Los Angeles (UTC-8/-7)</option>
                <option value="UTC">UTC</option>
              </select>
            </label>
          </div>
          <button type="submit" disabled={busy} className="btn-primary rounded-full px-6 py-2.5 text-sm font-medium disabled:opacity-60" data-testid="pe-submit">
            <Calculator size={16} weight="duotone" className="inline mr-2" />
            {busy ? "Mapping all 9 grahas..." : "Reveal all 9 planet effects"}
          </button>
        </form>

        {/* Result */}
        {result && (
          <div className="mt-10 space-y-6" data-testid="pe-result">
            {/* Hero */}
            <div className="rounded-2xl bg-gradient-to-br from-[#3F2D1B] via-[#5a3e22] to-[#7c4f2a] text-[#FAF4E8] p-6 sm:p-8 shadow-xl border border-amber-700/40">
              <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-widest mb-2">
                <Star size={14} weight="fill" /> Lagna {result.lagna.rashi} · 9 grahas mapped
              </div>
              <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight">
                Your <span className="italic text-amber-300">planetary signature</span>
              </h2>
              <p className="mt-3 text-amber-100/90 text-sm sm:text-base leading-relaxed max-w-2xl">
                Each card below shows a graha's house + sign, its dignity status (exalted / debilitated / own / neutral), the practical effect, and a tailored remedy block.
              </p>
            </div>

            {/* Free preview — first 3 planets (Sun, Moon, Mars) */}
            <div className="space-y-4">
              {result.planets.slice(0, FREE_PLANET_COUNT).map((p) => (
                <PlanetCard key={p.planet} planet={p} />
              ))}
            </div>

            {/* Paywall: remaining 6 planets */}
            <PaywallGate
              tool="planet_effects"
              toolLabel="9-Planet Effects & Remedies"
              birthDetails={form}
              report={result}
            >
              <div className="space-y-4">
                {result.planets.slice(FREE_PLANET_COUNT).map((p) => (
                  <PlanetCard key={p.planet} planet={p} defaultOpen={false} />
                ))}
              </div>
            </PaywallGate>

            {/* CTA — Mantra & Remedies specialist */}
            <ToolAstrologerCta
              requiredTechnique="Mantra Therapy & Planetary Remedies"
              pitch={`Want a live read on your debilitated / retrograde grahas — with the exact mantra-japa & remedy protocol for your dasha?`}
              ctaLabel="Talk to"
            />
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

// ---------- Sub-components ----------

const STATUS_STYLE = {
  exalted:       { bg: "bg-emerald-100", border: "border-emerald-300", text: "text-emerald-900", label: "EXALTED" },
  debilitated:   { bg: "bg-rose-100",    border: "border-rose-300",    text: "text-rose-900",    label: "DEBILITATED" },
  "in own sign": { bg: "bg-amber-100",   border: "border-amber-300",   text: "text-amber-900",   label: "OWN SIGN" },
  neutral:       { bg: "bg-slate-100",   border: "border-slate-300",   text: "text-slate-800",   label: "NEUTRAL" },
};

function PlanetCard({ planet, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const st = STATUS_STYLE[planet.status] || STATUS_STYLE.neutral;

  return (
    <div
      className="card-surface rounded-2xl overflow-hidden border border-soft"
      data-testid={`pe-planet-${planet.planet.toLowerCase()}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full p-4 sm:p-5 flex items-center gap-3 sm:gap-4 hover:bg-amber-50/40 transition-colors text-left"
      >
        <div
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white shrink-0 font-serif-display text-base sm:text-lg"
          style={{ backgroundColor: planet.color }}
        >
          {planet.planet.slice(0, 2)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-serif-display text-lg sm:text-xl text-[#3F2D1B]">{planet.name}</span>
            {planet.retrograde && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-mono">R retrograde</span>}
          </div>
          <div className="text-xs sm:text-sm text-muted mt-0.5">
            House <b className="text-[#3F2D1B]">{planet.house}</b> · <b className="text-[#3F2D1B]">{planet.rashi}</b> {planet.degrees_in_rashi != null && `(${planet.degrees_in_rashi.toFixed?.(1) ?? planet.degrees_in_rashi}°)`} · Nakshatra <b className="text-[#3F2D1B]">{planet.nakshatra}</b>
          </div>
        </div>
        <span className={`text-[10px] px-2 py-1 rounded-full ${st.bg} ${st.border} ${st.text} border font-bold tracking-widest shrink-0`}>
          {st.label}
        </span>
        <CaretDown size={16} weight="bold" className={`text-muted shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 sm:px-5 pb-5 pt-1 space-y-4 border-t border-soft">
          <div>
            <div className="label-eyebrow !text-[10px] mt-3">Karaka · what this planet rules</div>
            <p className="text-xs sm:text-sm mt-1 text-[#3F2D1B]/85 leading-snug">{planet.karaka}</p>
          </div>

          <div>
            <div className="label-eyebrow !text-[10px] text-gold">Effect in your chart</div>
            <p className="text-sm sm:text-base mt-1 text-[#3F2D1B] leading-relaxed">{planet.effect}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-xl bg-emerald-50/60 border border-emerald-200 p-3">
              <div className="label-eyebrow !text-[10px] text-emerald-700">Gifts when strong</div>
              <p className="text-xs text-emerald-950 mt-1 leading-snug">{planet.gifts}</p>
            </div>
            <div className="rounded-xl bg-rose-50/60 border border-rose-200 p-3">
              <div className="label-eyebrow !text-[10px] text-rose-700">Shadow when afflicted</div>
              <p className="text-xs text-rose-950 mt-1 leading-snug">{planet.shadows}</p>
            </div>
          </div>

          {/* Remedies */}
          <div className="rounded-xl bg-amber-50/70 border border-amber-200 p-4">
            <div className="label-eyebrow !text-[11px] text-amber-900 flex items-center gap-1.5">
              <Sparkle size={12} weight="fill" /> Classical remedies for {planet.planet}
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Remedy icon={Sun} label="Weekday" value={planet.remedies.weekday} />
              <Remedy icon={Star} label="Deity" value={planet.remedies.deity} />
              <Remedy icon={MoonIcon} label="Mantra" value={planet.remedies.mantra} wide />
              <Remedy icon={Sparkle} label="Gemstone" value={planet.remedies.gemstone} wide />
              <Remedy icon={Gift} label="Charity" value={planet.remedies.charity} />
              <Remedy icon={ForkKnife} label="Food" value={planet.remedies.food} />
              <Remedy icon={Hand} label="Behaviour" value={planet.remedies.behaviour} />
              <Remedy icon={ChartBar} label="Fast" value={planet.remedies.fast} />
            </div>
            <p className="text-[10px] text-amber-900/70 mt-3 italic">
              Always consult a qualified astrologer before wearing gemstones — wrong stones can amplify the shadow.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function Remedy({ icon: Icon, label, value, wide }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <div className="flex items-center gap-1.5 label-eyebrow !text-[10px] text-[#3F2D1B]/70">
        <Icon size={11} weight="duotone" className="text-gold" /> {label}
      </div>
      <div className="mt-1 text-xs sm:text-sm text-[#3F2D1B] leading-snug">{value}</div>
    </div>
  );
}
